import React, { CSSProperties, forwardRef } from "react";
import { Link as GatsbyLink } from "gatsby";
import clsx from "clsx";

import { isAstroPath } from "@constants/astroMigration";

type LinkProps = {
  to: string;
  color?: string;
  lightBlue?: boolean;
  darkBlue?: boolean;
  underlineOnly?: boolean;
  children?: React.ReactNode;
  customStyle?: CSSProperties;
  ariaLabel?: string;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
  newTab?: boolean;
  className?: string;
  /** Stretch the link to fill its container's width, e.g. for a full-bleed banner click area. */
  fullWidth?: boolean;
};

const Link = forwardRef<HTMLAnchorElement, LinkProps & React.HTMLAttributes<HTMLAnchorElement>>(
  (
    {
      to = "#",
      color = "inherit",
      lightBlue = false,
      darkBlue = false,
      underlineOnly = false,
      children,
      ariaLabel,
      customStyle,
      onClick,
      newTab = false,
      className,
      fullWidth = false,
      ...props
    },
    ref,
  ) => {
    const styles = {
      color: lightBlue ? "#12b8f6" : color,
      textDecoration: "none",
      ...(fullWidth && { width: "100%" }),
      ...customStyle,
    };

    let finalClassName = className || "";

    if (darkBlue) {
      finalClassName = clsx(className, "text-blue-600 hover:text-blue-500 underline");
    }

    if (underlineOnly) {
      finalClassName = clsx(className, "underline");
    }

    const isExternal = to.includes("http") || to.includes("mailto") || to.startsWith("tel") || newTab;

    if (isExternal) {
      return (
        <a
          ref={ref}
          href={to}
          target="_blank"
          rel="noreferrer"
          aria-label={ariaLabel}
          style={!finalClassName ? styles : undefined}
          className={finalClassName}
          onClick={(e) => {
            if (typeof window !== "undefined") {
              (window as any)?.plausible?.("External-Link-Click", { props: { url: to } });
            }
            onClick?.(e);
          }}
          {...props}
        >
          {children}
        </a>
      );
    }

    // /inspiration is served by a separate Cloudflare Worker (Astro), not this Gatsby app.
    // A GatsbyLink would try to client-side route it instead of letting Cloudflare route to that worker.
    const isInspiration = to.startsWith("/inspiration");

    if (isInspiration || isAstroPath(to)) {
      return (
        <a
          ref={ref}
          href={to}
          aria-label={ariaLabel}
          style={!finalClassName ? styles : undefined}
          className={finalClassName}
          onClick={onClick}
          {...props}
        >
          {children}
        </a>
      );
    }

    return (
      <GatsbyLink
        ref={ref as any} // GatsbyLink doesn't have perfect ref types
        to={to}
        aria-label={ariaLabel}
        style={!finalClassName ? styles : undefined}
        onClick={onClick}
        className={finalClassName}
        {...props}
      >
        {children}
      </GatsbyLink>
    );
  },
);

Link.displayName = "Link";

export default Link;
